import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Lente, useArrasteQueTrava } from '@/components/ui/lente';

const chaveDoItem = (item: HTMLElement) => item.dataset.itemMenu ?? '';

/**
 * O realce do item ativo na lateral (aluno e admin), no mesmo motor das abas e
 * da barra de baixo (`useArrasteQueTrava`): ao trocar de página ele desliza até
 * o item novo, esticando no caminho, e passa um tico antes de assentar.
 *
 * Dá para segurar o item selecionado — com o mouse ou com o dedo, na gaveta do
 * celular — e arrastar o realce pelo menu. Ele trava no item mais perto (passou
 * da metade, já é o de baixo) e, ao soltar, abre o item onde travou; os
 * externos (Comunidade, Central de ajuda) abrem numa aba nova, como no clique.
 * Arrastar nos outros itens continua rolando o menu.
 *
 * Os itens do menu se marcam com `data-item-menu="<rota ou url>"` (e
 * `data-externo` quando abrem fora); o realce é um só, desenhado atrás deles
 * dentro do <nav>.
 */
export function useRealceDoMenu(ativo: string | null, classeRealce: string) {
  const navigate = useNavigate();

  const motor = useArrasteQueTrava<HTMLElement>({
    eixo: 'y',
    seletor: '[data-item-menu]',
    chave: chaveDoItem,
    atual: ativo,
    pega: 'atual',
    aoEscolher: (_, item) => {
      const destino = chaveDoItem(item);
      if (!destino || destino === ativo) return;
      if (item.dataset.externo !== undefined) {
        window.open(destino, '_blank', 'noopener,noreferrer');
        return;
      }
      navigate(destino);
    },
  });

  // o item clicado acende na hora, antes de a página nova terminar de abrir
  const aoClicar = (e: React.MouseEvent) => {
    motor.props.onClickCapture(e);
    if (e.defaultPrevented) return;
    const item = (e.target as Element).closest<HTMLElement>('[data-item-menu]');
    if (!item || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
    if (item.dataset.externo !== undefined) return;
    const i = motor.chaves.indexOf(chaveDoItem(item));
    if (i >= 0) motor.acender(i);
  };

  const realce = (
    <Lente
      eixo="y"
      caixa={motor.caixa}
      arrastando={motor.arrastando}
      apertada={motor.apertado}
      className={cn('select-none', classeRealce)}
    />
  );

  return {
    navRef: motor.ref,
    navProps: { ...motor.props, onClickCapture: aoClicar },
    realce,
    /** O item que aparece aceso agora (o de baixo do realce, se arrastando). */
    aceso: motor.lente !== null ? (motor.chaves[motor.lente] ?? null) : null,
    arrastando: motor.arrastando,
  };
}
